/**
 * ISSUE-1048. The message menu only offers a row where it does something.
 * `gif`, `poll`, `gameCard`, `pdf` and `systemAnnouncement` never draw a body
 * in `MessageBubble.tsx`, so they offer no Copy and no Edit. messaging-api's
 * `/message/edit` applies the same Edit rule and refuses every other kind.
 */
import type { typeMessageRow } from "./mergeMessagePages";
import { stripMentionMarkup } from "./messageText";

export type typeMessageActionInput = {
  kind: typeMessageRow["kind"];
  body: string | null;
};

/** Kinds whose bubble draws `body`: text draws it as the message, image draws it as the caption. */
export const COPYABLE_MESSAGE_KINDS: readonly typeMessageRow["kind"][] = [
  "text",
  "image",
];

/** ISSUE-1048 R4, owner default (b): a captioned photo is editable, nothing else beyond text. */
export const EDITABLE_MESSAGE_KINDS: readonly typeMessageRow["kind"][] = [
  "text",
  "image",
];

/** The text Copy puts on the clipboard, or `null` when the menu offers no Copy. */
export function resolveCopyText(
  payload: typeMessageActionInput,
): string | null {
  if (!COPYABLE_MESSAGE_KINDS.includes(payload.kind)) return null;
  if (payload.body === null) return null;
  const text = stripMentionMarkup(payload.body);
  if (text.trim().length === 0) return null;
  // Untrimmed on purpose: Copy on a text message copies exactly what it did before (R3).
  return text;
}

/**
 * ISSUE-1368 and ISSUE-1364. While staff have chat switched off for the
 * account, or an officer has the fan timed out in this chat, `/message/edit`
 * refuses (`CHAT_SUSPENDED`, `CHAT_TIMED_OUT`) the same as a send, so the
 * menu does not offer an Edit that can only fail.
 */
export function canEditMessage(
  payload: typeMessageActionInput & {
    isOwn: boolean;
    isPostingClosed: boolean;
  },
): boolean {
  return (
    payload.isOwn &&
    !payload.isPostingClosed &&
    EDITABLE_MESSAGE_KINDS.includes(payload.kind) &&
    payload.body !== null &&
    payload.body.trim().length > 0
  );
}
